import { SignedIn, SignedOut, UserButton, useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { Plus, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { primaryNavLinks } from "@/lib/navConfig";
import { memo, useMemo, useCallback } from "react";
import { WalletConnect } from "@/components/WalletConnect";
import { Skeleton } from "@/components/ui/skeleton";

interface NavbarProps {
  className?: string;
}

// Memoized nav links - computed once from static array
const publicNavLinks = primaryNavLinks.filter((link) => link.visibility === "all");
const signedInNavLinks = primaryNavLinks.filter((link) => link.visibility === "signedIn");

export const Navbar = memo(function Navbar({ className }: NavbarProps) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { isLoaded, isSignedIn } = useAuth();

  // Get unread message count
  const unreadCount = useQuery(api.messages.getUnreadCount) ?? 0;

  // Memoized active path checker
  const isActive = useCallback((path: string) => location === path, [location]);

  return (
    <nav className={`border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur ${className}`}>
      <div className="container flex items-center justify-between h-16">
        <Link href="/">
          <a className="text-2xl font-bold gradient-text">{t('common.appName')}</a>
        </Link>

        <div className="flex items-center gap-4">
          {publicNavLinks.map((link) => (
            <Link key={link.href} href={link.href}>
              <a className={`transition-colors ${isActive(link.href) ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>
                {t(link.translationKey)}
              </a>
            </Link>
          ))}

          {!isLoaded ? (
            <div className="flex items-center gap-2">
              <Skeleton className="w-8 h-8 rounded-full" />
              <Skeleton className="w-20 h-10 rounded-md" />
            </div>
          ) : (
            <>
              <SignedIn>
                <Link href="/post">
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.postAd')}</span>
                  </Button>
                </Link>

                {signedInNavLinks.map((link) => (
                  <Link key={link.href} href={link.href}>
                    <a className={`transition-colors ${isActive(link.href) ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>
                      {t(link.translationKey)}
                    </a>
                  </Link>
                ))}

                <Link href="/messages">
                  <a className="relative p-2 hover:bg-muted rounded-full transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-destructive text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </a>
                </Link>

                <div className="flex items-center gap-2">
                  <UserButton afterSignOutUrl="/" />
                </div>
              </SignedIn>

              <SignedOut>
                <div className="flex items-center gap-2">
                  <Link href="/sign-in">
                    <Button variant="ghost">{t('auth.signIn')}</Button>
                  </Link>
                  <Link href="/sign-up">
                    <Button>{t('home.getStarted')}</Button>
                  </Link>
                </div>
              </SignedOut>
            </>
          )}

          <WalletConnect variant="compact" />

          <LanguageSwitcher />
        </div>
      </div>
    </nav>
  );
});

Navbar.displayName = "Navbar";
